// Bespoke building prefabs: production, civilian and food buildings.
//
// Coordinate convention: origin at the building's base on the tile. Y up,
// X right, Z forward (toward camera). See buildings_extra.ts for the full
// group list; the shared `box()` helper and palette live in
// buildings_extra_shared.ts.

import { DARK_STONE, DARK_WOOD, STONE, WOOD, box } from './buildings_extra_shared.js';
import type { StructurePrefab } from './types.js';

// ── Production / Civilian ─────────────────────────────────────────────

export const forgeHut: StructurePrefab = {
  name: 'Forge Hut',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 3, 5, WOOD),
    ...box(-2, 4, -2, 5, 1, 5, '#8b1a1a'),
    // Chimney
    ...box(-2, 1, -2, 1, 5, 1, DARK_STONE),
    // Hearth glow
    ...box(-1, 1, -2, 3, 1, 1, '#ff6622'),
    // Anvil
    { x: 0, y: 1, z: 1, color: '#222222' },
    { x: 0, y: 2, z: 1, color: '#444444' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};

export const granary: StructurePrefab = {
  name: 'Granary',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Stone foundation
    ...box(-2, 1, -2, 5, 1, 5, '#888888'),
    // Cylindrical silo (approx with square + chamfer)
    ...box(-1, 2, -1, 3, 4, 3, '#c9a063'),
    ...box(-2, 2, -2, 1, 4, 1, '#c9a063'),
    ...box(2, 2, -2, 1, 4, 1, '#c9a063'),
    ...box(-2, 2, 2, 1, 4, 1, '#c9a063'),
    ...box(2, 2, 2, 1, 4, 1, '#c9a063'),
    // Conical roof
    ...box(-1, 6, -1, 3, 1, 3, '#5a3a1a'),
    ...box(0, 7, 0, 1, 1, 1, '#5a3a1a'),
    // Grain sacks at base
    { x: 2, y: 1, z: 0, color: '#d4a04a' },
    { x: -3, y: 1, z: 0, color: '#d4a04a' },
  ],
};

export const well: StructurePrefab = {
  name: 'Well',
  blocks: [
    ...box(-1, 0, -1, 3, 1, 3, STONE),
    ...box(-1, 1, -1, 3, 2, 3, DARK_STONE, 'stone'),
    // Hollow center for water
    { x: 0, y: 2, z: 0, color: '#3a5a8a' },
    // Wooden posts
    { x: -2, y: 3, z: -2, color: WOOD },
    { x: 2, y: 3, z: -2, color: WOOD },
    { x: -2, y: 4, z: -2, color: WOOD },
    { x: 2, y: 4, z: -2, color: WOOD },
    // Crossbeam
    ...box(-2, 5, -2, 5, 1, 1, WOOD),
    // Roof
    ...box(-2, 6, -2, 5, 1, 3, '#5a3a1a'),
    ...box(-1, 7, -1, 3, 1, 1, '#5a3a1a'),
    // Bucket on rope
    { x: 0, y: 3, z: 0, color: '#704030' },
  ],
};

export const herbalistHut: StructurePrefab = {
  name: "Herbalist's Hut",
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 3, 5, WOOD),
    // Mossy green roof
    ...box(-2, 4, -2, 5, 1, 5, '#4a7c4a'),
    ...box(-1, 5, -1, 3, 1, 3, '#4a7c4a'),
    ...box(0, 6, 0, 1, 1, 1, '#4a7c4a'),
    // Drying herbs hanging
    { x: 1, y: 4, z: 2, color: '#88aa44' },
    { x: -1, y: 4, z: 2, color: '#aa6644' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
    // Planter boxes
    ...box(-3, 1, 1, 1, 1, 1, '#4a7c4a'),
    ...box(3, 1, 1, 1, 1, 1, '#4a7c4a'),
  ],
};

export const library: StructurePrefab = {
  name: 'Library',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Main reading room
    ...box(-2, 1, -2, 5, 4, 5, '#aa8866'),
    // Tower with scrolls
    ...box(-2, 1, -2, 1, 7, 1, STONE),
    ...box(2, 1, -2, 1, 7, 1, STONE),
    ...box(-2, 1, 2, 1, 7, 1, STONE),
    ...box(2, 1, 2, 1, 7, 1, STONE),
    // Spire
    ...box(-1, 8, -1, 3, 1, 3, DARK_STONE),
    ...box(0, 9, 0, 1, 2, 1, DARK_STONE),
    // Windows with light
    ...box(-1, 2, 2, 3, 1, 1, '#88ccff'),
    ...box(-1, 3, 2, 3, 1, 1, '#88ccff'),
    // Book spines on shelves visible
    { x: -1, y: 2, z: -1, color: '#aa3333' },
    { x: 0, y: 2, z: -1, color: '#3333aa' },
    { x: 1, y: 2, z: -1, color: '#33aa33' },
    { x: -1, y: 3, z: -1, color: '#aaaa33' },
    { x: 0, y: 3, z: -1, color: '#aa33aa' },
    { x: 1, y: 3, z: -1, color: '#33aaaa' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};

export const bank: StructurePrefab = {
  name: 'Bank',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, DARK_STONE),
    ...box(-2, 1, -2, 5, 4, 5, STONE, 'stone'),
    // Marble columns at corners
    { x: -2, y: 1, z: -2, color: '#e0e0e0' },
    { x: 2, y: 1, z: -2, color: '#e0e0e0' },
    { x: -2, y: 1, z: 2, color: '#e0e0e0' },
    { x: 2, y: 1, z: 2, color: '#e0e0e0' },
    { x: -2, y: 2, z: -2, color: '#e0e0e0' },
    { x: 2, y: 2, z: -2, color: '#e0e0e0' },
    { x: -2, y: 2, z: 2, color: '#e0e0e0' },
    { x: 2, y: 2, z: 2, color: '#e0e0e0' },
    { x: -2, y: 3, z: -2, color: '#e0e0e0' },
    { x: 2, y: 3, z: -2, color: '#e0e0e0' },
    { x: -2, y: 3, z: 2, color: '#e0e0e0' },
    { x: 2, y: 3, z: 2, color: '#e0e0e0' },
    // Gold-trimmed cornice
    ...box(-2, 4, -2, 5, 1, 5, '#d4af37'),
    // Pediment
    ...box(-2, 5, -2, 5, 1, 3, '#e0e0e0'),
    ...box(-1, 6, -1, 3, 1, 1, '#e0e0e0'),
    // Coin emblem
    { x: 0, y: 5, z: 0, color: '#ffcc44' },
    // Vault door
    ...box(0, 1, 2, 1, 2, 1, '#444444'),
    { x: 0, y: 2, z: 2, color: '#d4af37' },
  ],
};

// ── Food ────────────────────────────────────────────────────────────────

export const pasture: StructurePrefab = {
  name: 'Pasture',
  blocks: [
    // Grassy base
    ...box(-3, 0, -3, 7, 1, 7, '#4a7c3a'),
    // Fence rails (perimeter)
    ...box(-3, 1, -3, 7, 1, 1, WOOD),
    ...box(-3, 1, 3, 7, 1, 1, WOOD),
    ...box(-3, 1, -3, 1, 1, 7, WOOD),
    ...box(3, 1, -3, 1, 1, 7, WOOD),
    ...box(-3, 2, -3, 7, 1, 1, WOOD),
    ...box(-3, 2, 3, 7, 1, 1, WOOD),
    ...box(-3, 2, -3, 1, 1, 7, WOOD),
    ...box(3, 2, -3, 1, 1, 7, WOOD),
    // Fence posts (corners)
    { x: -3, y: 3, z: -3, color: WOOD },
    { x: 3, y: 3, z: -3, color: WOOD },
    { x: -3, y: 3, z: 3, color: WOOD },
    { x: 3, y: 3, z: 3, color: WOOD },
    // Sheep (3-voxel body + head)
    { x: -1, y: 1, z: 0, color: '#eeeeee' },
    { x: 0, y: 1, z: 0, color: '#eeeeee' },
    { x: 0, y: 2, z: 0, color: '#eeeeee' },
    { x: 1, y: 2, z: 0, color: '#444444' },
    { x: -2, y: 1, z: 2, color: '#eeeeee' },
    { x: -1, y: 1, z: 2, color: '#eeeeee' },
    { x: -2, y: 2, z: 2, color: '#eeeeee' },
    { x: -3, y: 2, z: 2, color: '#444444' },
  ],
};

export const winery: StructurePrefab = {
  name: 'Winery',
  blocks: [
    // Stone press base
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    ...box(-2, 1, -2, 5, 2, 5, '#aa6644'),
    // Thatched roof
    ...box(-2, 3, -2, 5, 1, 5, '#c9a063'),
    ...box(-1, 4, -1, 3, 1, 3, '#c9a063'),
    ...box(0, 5, 0, 1, 1, 1, '#c9a063'),
    // Wine press (large cask)
    { x: -1, y: 1, z: 1, color: '#5a3a1a' },
    { x: 0, y: 1, z: 1, color: '#5a3a1a' },
    { x: 1, y: 1, z: 1, color: '#5a3a1a' },
    { x: 0, y: 1, z: 2, color: '#8b1a1a' },
    // Vine trellis (T-posts with purple grapes)
    { x: -3, y: 1, z: -3, color: WOOD },
    { x: -3, y: 2, z: -3, color: WOOD },
    { x: -3, y: 3, z: -3, color: WOOD },
    { x: -2, y: 3, z: -3, color: '#5a2a5a' },
    { x: -1, y: 3, z: -3, color: '#5a2a5a' },
    { x: 3, y: 1, z: -3, color: WOOD },
    { x: 3, y: 2, z: -3, color: WOOD },
    { x: 3, y: 3, z: -3, color: WOOD },
    { x: 2, y: 3, z: -3, color: '#5a2a5a' },
    { x: 1, y: 3, z: -3, color: '#5a2a5a' },
  ],
};

export const brewery: StructurePrefab = {
  name: 'Brewery',
  blocks: [
    ...box(-2, 0, -2, 5, 1, 5, STONE),
    // Tall brewhouse
    ...box(-2, 1, -2, 5, 4, 5, '#704030'),
    ...box(-2, 5, -2, 5, 1, 5, '#5a3a1a'),
    // Chimney (steam vent)
    ...box(2, 1, -2, 1, 6, 1, '#444444'),
    // Copper brewing vats visible
    { x: -1, y: 1, z: 2, color: '#c87838' },
    { x: 0, y: 1, z: 2, color: '#c87838' },
    { x: -1, y: 2, z: 2, color: '#c87838' },
    { x: 0, y: 2, z: 2, color: '#c87838' },
    // Beer barrel stack
    { x: -3, y: 1, z: 1, color: '#8b4513' },
    { x: -3, y: 2, z: 1, color: '#8b4513' },
    { x: -3, y: 1, z: 0, color: '#8b4513' },
    { x: -3, y: 2, z: 0, color: '#8b4513' },
    // Door
    ...box(0, 1, 2, 1, 2, 1, DARK_WOOD),
  ],
};
